import { useMutation, useQuery } from '@apollo/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMPANY_ENROLLMENT_STATUS_NEED_ACTIVATION,
  COMPANY_KSUID_STORAGE_KEY,
  TRANSLATIONS_NAMESPACE,
} from '../constants';

import ACTIVATE_EMPLOYEE from '../mutations/activateEmployee';
import ENROLLED_COMPANIES from '../queries/enrolledCompanies';
import { formatCompanyData, isAllowedToLogin } from '../utils';

/**
 * A hook for handling company-based authorization
 *
 * @param {object} prop - useCompanyAuth props
 * @param {object} prop.apiClient - client for apollo graphql hooks
 * @param {Function} prop.onSelectCompanySuccess - callback after select company process succeed
 * @param {localStorage} prop.persistentDataStorage - where to store selected company data
 * @param {string} prop.platformAccess - kind of platform the client is hitting
 * @returns {object} - useful states and functions
 */
const useCompanyAuth = ({
  apiClient: client,
  onSelectCompanySuccess,
  persistentDataStorage,
  platformAccess,
}) => {
  const { t } = useTranslation([TRANSLATIONS_NAMESPACE]);
  const [enrolledCompanies, setEnrolledCompanies] = useState([]);
  const [selectedCompanyKsuid, setSelectedCompanyKsuid] = useState(
    persistentDataStorage.getItem(COMPANY_KSUID_STORAGE_KEY)
  );

  const resetEnrolledCompanies = useCallback(() => setEnrolledCompanies([]), []);

  const [activateEmployee] = useMutation(ACTIVATE_EMPLOYEE, {
    ...(client ? { client } : {}),
    variables: {
      companyKsuid: selectedCompanyKsuid
    }
  });

  const getEnrolledCompanyByKsuid = useCallback(
    companyKsuid => enrolledCompanies.find(({ ksuid } = {}) => ksuid === companyKsuid),
    [enrolledCompanies]
  );

  const selectedCompany = useMemo(
    () => getEnrolledCompanyByKsuid(selectedCompanyKsuid) || {},
    [getEnrolledCompanyByKsuid, selectedCompanyKsuid]
  );

  // Using refetch and state for saving the query result is intended
  // to get the promise-based function.
  const { refetch: fetchEnrolledCompanies } = useQuery(ENROLLED_COMPANIES, {
    ...(client ? { client } : {}),
    fetchPolicy: 'no-cache',
    skip: true,
    notifyOnNetworkStatusChange: true,
    onError() {
      // do nothing
    }
  });

  const fetchAndSaveEnrolledCompanies = useCallback(async options => {
    const response = await fetchEnrolledCompanies({
      ...(platformAccess ? { platformAccess } : {}),
      ...options,
    });
    const allowedToLoginCompanies = (response?.data?.enrolledCompanies || [])
      .map(formatCompanyData)
      .filter(isAllowedToLogin);

    if (!allowedToLoginCompanies.length) {
      throw new Error(t('user_has_been_deactivated'));
    }

    setEnrolledCompanies(allowedToLoginCompanies);
    return allowedToLoginCompanies;
  }, [fetchEnrolledCompanies, platformAccess, t]);

  const resetSelectedCompany = useCallback(() => {
    setSelectedCompanyKsuid(null);
    persistentDataStorage.removeItem(COMPANY_KSUID_STORAGE_KEY);
  }, [persistentDataStorage]);

  const setSelectedCompany = useCallback(({ ksuid: companyKsuid } = {}) => {
    if (getEnrolledCompanyByKsuid(companyKsuid)) {
      persistentDataStorage.setItem(COMPANY_KSUID_STORAGE_KEY, companyKsuid);
      setSelectedCompanyKsuid(companyKsuid);
      onSelectCompanySuccess();
    }
  }, [getEnrolledCompanyByKsuid, onSelectCompanySuccess, persistentDataStorage]);

  useEffect(() => {
    if (selectedCompany?.status === COMPANY_ENROLLMENT_STATUS_NEED_ACTIVATION) {
      activateEmployee();
    }
  }, [activateEmployee, selectedCompany]);

  useEffect(() => {
    if (enrolledCompanies.length === 1) {
      setSelectedCompany(enrolledCompanies[0]);
    }
  }, [enrolledCompanies, setSelectedCompany]);

  return {
    activateEmployee,
    enrolledCompanies,
    fetchAndSaveEnrolledCompanies,
    resetEnrolledCompanies,
    resetSelectedCompany,
    selectedCompany,
    selectedCompanyKsuid,
    setSelectedCompany,
  };
};

export default useCompanyAuth;
